import { motion } from "framer-motion";
import { Brain, Code, Users, Zap, Globe, FileCode, ArrowRight, Sparkles, Target, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Footer } from "@/components/utilities/Footer";
import { useEffect } from "react";
import { useTheme } from "@/contexts/ThemeContext";

const AboutUs = ({ className = "" }: { className?: string }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  // Make sure we scroll to top when this page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Animation variants for container
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { 
        staggerChildren: 0.1, 
        delayChildren: 0.2
      }
    },
  };

  // Animation variants for individual items
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1, 
      transition: { 
        type: "spring", 
        stiffness: 100
      }
    },
  };

  const handleGetStarted = () => {
    navigate('/auth/login');
  };

  const features = [
    {
      icon: <Brain className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "Smart",
      description: "So you don't need to explain your code twice"
    },
    {
      icon: <Zap className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "Instant",
      description: "So you can spend time building, not writing"
    },
    {
      icon: <Globe className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "Context-aware",
      description: "So it understands not just the code but the why"
    },
    {
      icon: <Users className={theme === 'dark' ? "text-primary" : "text-primary-light"} />,
      title: "Collaborative",
      description: "So your whole team stays in sync"
    },
  ];

  const offerings = [
    "Clear, natural-language documentation",
    "Instant summaries and walkthroughs of complex files",
    "Snippet-level insights and debugging help",
    "AI-generated code additions and improvements",
    "Seamless integration with platforms like Notion, Jira, GitHub, and more"
  ];

  const differentiators = [
    {
      title: "AI-First",
      description: "Powered by advanced LLMs trained specifically for code understanding"
    },
    {
      title: "Always Synchronized",
      description: "Automatically updates with code changes"
    },
    {
      title: "Integrated into Workflow",
      description: "Connects to GitHub, Jira, Notion, and more"
    },
    {
      title: "Grows with You",
      description: "Learns from your codebase and improves over time"
    },
    {
      title: "More Than Docs",
      description: "Helps you debug, review, and generate code too"
    }
  ];

  const userTypes = [
    "A solo developer managing multiple side projects",
    "A startup team pushing features fast",
    "A mid-size company struggling with technical debt",
    "A large engineering org trying to onboard devs quickly"
  ];

  const roadmapItems = [
    "Deeper GitHub automation & CI/CD support",
    "Documentation analytics for teams & managers",
    "Custom API workflows for enterprise use",
    "Personalized AI agents for specific repos",
    "Knowledge base auto-generation"
  ];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#030405]' : 'bg-background'} ${theme === 'dark' ? 'text-white' : 'text-gray-800'} relative overflow-hidden flex flex-col`}>
      {/* Background elements */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {theme === 'dark' && (
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f25_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f25_1px,transparent_1px)] bg-[size:15px_15px] opacity-40"></div>
        )}
        
        {theme !== 'dark' && (
          <div className={`absolute inset-0 light-grid-pattern bg-[size:14px_24px]`} />
        )}
        
        <motion.div
          className={`absolute top-1/4 -right-40 w-96 h-96 rounded-full blur-3xl ${theme === 'dark'
            ? 'bg-gradient-to-r from-primary/20 to-purple-500/10'
            : 'bg-gradient-to-r from-primary-light/20 to-purple-500/10'}`}
          animate={{ x: [0, 60, 0], y: [0, -40, 0], scale: [1, 1.2, 1], opacity: [0.5, 0.7, 0.5] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute -top-20 left-1/3 w-72 h-72 rounded-full blur-3xl ${theme === 'dark'
            ? 'bg-gradient-to-r from-blue-500/15 to-primary/10'
            : 'bg-gradient-to-r from-blue-500/15 to-primary-light/10'}`}
          animate={{ x: [-20, 40, -20], y: [10, 40, 10], scale: [1.1, 0.9, 1.1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10 pt-16 sm:pt-20 flex-grow">
        <motion.div
          className={`py-16 ${className}`}
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Hero Section */}
          <div className="text-center mb-20">
            <motion.div
              variants={itemVariants}
              className={`inline-flex items-center px-4 py-2 rounded-full ${theme === 'dark'
                ? 'bg-primary/10 border border-primary/20'
                : 'bg-primary-light/10 border border-primary-light/20'} mb-6`}
            >
              <Sparkles className={`h-4 w-4 mr-2 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'}`} />
              <span className={`text-sm font-medium ${theme === 'dark' ? 'text-primary' : 'text-primary-light'}`}>
                About DocGen
              </span>
            </motion.div>
            
            <motion.h1
              variants={itemVariants}
              className={`text-5xl sm:text-6xl lg:text-7xl font-bold ${theme === 'dark'
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400'
                : 'text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-600'} mb-6`}
            >
              Turning Code Into
              <br />
              <span className={`text-transparent bg-clip-text ${theme === 'dark'
                ? 'bg-gradient-to-r from-primary to-blue-400'
                : 'bg-gradient-to-r from-primary-light to-blue-500'}`}>
                Clarity
              </span> - Instantly
            </motion.h1>
            
            <motion.p
              variants={itemVariants}
              className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-xl max-w-3xl mx-auto mb-8 leading-relaxed`}
            >
              At DocGen, we're building the future of developer productivity - one auto-generated line of
              documentation at a time. Our AI-powered platform transforms complex codebases into crystal-clear
              documentation, helping developers and teams communicate better, ship faster, and collaborate smarter.
            </motion.p>
          </div>

          {/* Our Story Section */}
          <motion.div variants={itemVariants} className="mb-20">
            <div className={`rounded-2xl ${theme === 'dark'
              ? 'bg-[#050607]/70 border border-gray-800/50'
              : 'bg-white/80 border border-gray-200'} p-8 md:p-12 ${theme === 'dark' ? '' : 'light-card-shadow'}`}>
              <div className="flex items-center mb-6">
                <Target className={`h-8 w-8 mr-4 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'}`} />
                <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark'
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-blue-500'}`}>
                  Our Story
                </h2>
              </div>
              <h3 className={`text-2xl font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                Built by Developers. Backed by Vision.
              </h3>
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg leading-relaxed space-y-6`}>
                <p>
                  The idea behind DocGen didn't come from a boardroom - it came from a deep personal pain
                  point experienced by developers in the trenches.
                </p>
                <p>
                  The original concept was sparked by Aniket Singh, a passionate AI Developer and proud graduate
                  of IIT Dhanbad. During his time working with an AI company post-graduation, Aniket constantly
                  found himself and his colleagues bogged down by repetitive documentation tasks, poor code
                  handovers, and a frustrating lack of transparency between developers on the same project. The
                  more he dug into it, the clearer it became - developers weren't just writing code; they were
                  constantly writing explanations. That's where the seed of DocGen was planted.
                </p>
                <p>
                  Aniket envisioned a world where documentation could be intelligent, instantaneous, and integrated
                  into the way developers actually work. He began building a prototype, but he knew he couldn't do it
                  alone.
                </p>
                <p>
                  Soon after, he was joined by Varshith Gudur, a skilled Data and AI Engineer with hands-on
                  experience at a U.S.-based data science company. Varshith's engineering depth supercharged the
                  capabilities of DocGen, enabling it to analyze code contextually and generate not just static
                  documentation - but dynamic, actionable insights that evolve with the codebase.
                </p>
                <p>
                  But innovation alone isn't enough to scale impact. That's when Kritarth Mahalwal came on board - a
                  Business Development leader with over 5 years of experience in growth strategy, B2B marketing,
                  and finance. With Kritarth leading the business side, DocGen started shaping into not just a
                  product - but a movement to redefine how modern development teams document and deliver
                  software.
                </p>
                <p>
                  From a humble idea to a rapidly evolving platform, DocGen is now used by developers, teams,
                  and tech-forward companies seeking a better, faster way to understand, explain, and scale their code.
                </p>
              </div>
            </div>
          </motion.div>

          {/* What We Do Section */}
          <motion.div variants={itemVariants} className="mb-20">
            <div className="text-center mb-12">
              <h2 className={`text-4xl md:text-5xl font-bold ${theme === 'dark'
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400'
                : 'text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-600'} mb-4`}>
                What We Do
              </h2>
              <p className={`text-2xl font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-8`}>
                Documentation that Works as Fast as You Code
              </p>
            </div>
            
            <div className={`rounded-2xl ${theme === 'dark'
              ? 'bg-[#050607]/70 border border-gray-800/50'
              : 'bg-white/80 border border-gray-200'} p-8 md:p-12 ${theme === 'dark' ? '' : 'light-card-shadow'}`}>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg mb-8`}>
              DocGen is an AI-powered documentation platform that takes in your code and gives you back:
              </p>
              <ul className="space-y-4">
                {offerings.map((offering, index) => (
                  <li key={index} className="flex items-start">
                    <ArrowRight className={`h-5 w-5 mr-3 mt-1 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'} flex-shrink-0`} />
                    <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-lg`}>{offering}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Why We Built Docgen Section */}
          <motion.div variants={itemVariants} className="mb-20">
            <div className={`rounded-2xl ${theme === 'dark'
              ? 'bg-[#050607]/70 border border-gray-800/50'
              : 'bg-white/80 border border-gray-200'} p-8 md:p-12 ${theme === 'dark' ? '' : 'light-card-shadow'}`}>
              <div className="flex items-center mb-6">
                <Lightbulb className={`h-8 w-8 mr-4 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'}`} />
                <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark'
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-blue-500'}`}>
                  Why We Built Docgen
                </h2>
              </div>
              <h3 className={`text-2xl font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                Code Shouldn't Need a Translator
              </h3>
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg leading-relaxed space-y-6`}>
                <p>
                  Documentation is often seen as a chore - something that slows you down, something you "get to
                  later." But poor or missing documentation is one of the biggest threats to developer velocity, team
                  productivity, and software quality.
                </p>
                <p>We built DocGen to solve that.</p>
                <p>
                  We don't believe documentation should be manual, repetitive, or disconnected from your workflow.
                  We believe it should be:
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                {features.map((feature, index) => (
                  <div key={index} className={`p-6 rounded-xl ${theme === 'dark'
                    ? 'bg-gray-900/30 border border-gray-700/30'
                    : 'bg-gray-50/80 border border-gray-200'}`}>
                    <div className="flex items-center mb-3">
                      {feature.icon}
                      <h4 className={`text-xl font-semibold ml-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                        {feature.title}
                      </h4>
                    </div>
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Who We Serve Section */}
          <motion.div variants={itemVariants} className="mb-20">
            <div className={`rounded-2xl ${theme === 'dark'
              ? 'bg-[#050607]/70 border border-gray-800/50'
              : 'bg-white/80 border border-gray-200'} p-8 md:p-12 ${theme === 'dark' ? '' : 'light-card-shadow'}`}>
              <div className="flex items-center mb-6">
                <Users className={`h-8 w-8 mr-4 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'}`} />
                <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark'
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-blue-500'}`}>
                  Who We Serve
                </h2>
              </div>
              <h3 className={`text-2xl font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                For Developers. By Developers.
              </h3>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg mb-6`}>
                Whether you're:
              </p>
              <ul className="space-y-4">
                {userTypes.map((userType, index) => (
                  <li key={index} className="flex items-start">
                    <ArrowRight className={`h-5 w-5 mr-3 mt-1 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'} flex-shrink-0`} />
                    <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-lg`}>{userType}</span>
                  </li>
                ))}
              </ul>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg mt-6`}>
              DocGen is built for you.
              </p>
            </div>
          </motion.div>

          {/* How We're Different Section */}
          <motion.div variants={itemVariants} className="mb-20">
            <div className={`rounded-2xl ${theme === 'dark'
              ? 'bg-[#050607]/70 border border-gray-800/50'
              : 'bg-white/80 border border-gray-200'} p-8 md:p-12 ${theme === 'dark' ? '' : 'light-card-shadow'}`}>
              <div className="flex items-center mb-6">
                <Sparkles className={`h-8 w-8 mr-4 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'}`} />
                <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark'
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-blue-500'}`}>
                  How We're Different
                </h2>
              </div>
              <h3 className={`text-2xl font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                Not Just Another Doc Tool - Your AI Dev Assistant
              </h3>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg mb-8`}>
                Unlike traditional documentation tools that require templates, manual input, or time-consuming
                setup, DocGen is plug-and-play:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {differentiators.map((diff, index) => (
                  <div key={index} className={`p-6 rounded-xl ${theme === 'dark'
                    ? 'bg-gray-900/30 border border-gray-700/30'
                    : 'bg-gray-50/80 border border-gray-200'}`}>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                      {diff.title}
                    </h4>
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {diff.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Our Philosophy Section */}
          <motion.div variants={itemVariants} className="mb-20">
            <div className={`rounded-2xl ${theme === 'dark'
              ? 'bg-[#050607]/70 border border-gray-800/50'
              : 'bg-white/80 border border-gray-200'} p-8 md:p-12 ${theme === 'dark' ? '' : 'light-card-shadow'}`}>
              <div className="flex items-center mb-6">
                <Code className={`h-8 w-8 mr-4 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'}`} />
                <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark'
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-blue-500'}`}>
                  Our Philosophy
                </h2>
              </div>
              <h3 className={`text-2xl font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                Make Code Human Again.
              </h3>
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg leading-relaxed space-y-6`}>
                <p>
                  Behind every codebase is a team. Behind every team is communication. We believe documentation
                  is a core part of that - and it deserves better tools.
                </p>
                <p>
                  Our mission is simple: make every line of code easier to understand, easier to share, and easier to
                  grow.
                </p>
              </div>
            </div>
          </motion.div>

          {/* What's Next Section */}
          <motion.div variants={itemVariants} className="mb-20">
            <div className={`rounded-2xl ${theme === 'dark'
              ? 'bg-[#050607]/70 border border-gray-800/50'
              : 'bg-white/80 border border-gray-200'} p-8 md:p-12 ${theme === 'dark' ? '' : 'light-card-shadow'}`}>
              <div className="flex items-center mb-6">
                <ArrowRight className={`h-8 w-8 mr-4 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'}`} />
                <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark'
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-blue-500'}`}>
                  What's Next
                </h2>
              </div>
              <h3 className={`text-2xl font-semibold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                We're just getting started.
              </h3>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-lg mb-8`}>
                As we continue building DocGen, we're focusing on:
              </p>
              <ul className="space-y-4">
                {roadmapItems.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <ArrowRight className={`h-5 w-5 mr-3 mt-1 ${theme === 'dark' ? 'text-primary' : 'text-primary-light'} flex-shrink-0`} />
                    <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-lg`}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div 
            variants={itemVariants} 
            className="text-center"
          >
            <div className={`rounded-2xl ${theme === 'dark'
              ? 'bg-gradient-to-br from-primary/10 to-blue-500/10 border border-primary/20'
              : 'bg-gradient-to-br from-primary-light/10 to-blue-500/10 border border-primary-light/20'} p-8 md:p-12`}>
              <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark'
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400'
                : 'text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-blue-500'} mb-6`}>
                Ready to Transform Your Documentation?
              </h2>
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-xl mb-8 max-w-2xl mx-auto`}>
                Join thousands of developers who've already made the switch to intelligent, automated documentation.
              </p>
              <Button
                onClick={handleGetStarted}
                size="lg"
                className={`${theme === 'dark'
                  ? 'bg-primary hover:bg-primary/90 text-black'
                  : 'bg-primary-light hover:bg-primary-light/90 text-white'} text-lg px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105`}
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AboutUs; 